import React, { useMemo } from "react";

import { cn } from "~/lib/cn";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: unknown;
  active?: boolean;
  onValueChange?: (value: string) => void;
};

/** The standard text input, with an error state. */
export const Input = React.memo(
  React.forwardRef<HTMLInputElement, InputProps>(
    (
      {
        type,
        className,
        error,
        active,
        onValueChange,
        onChange,
        maxLength,
        value,
        ...props
      },
      ref,
    ) => {
      const currentLength = String(value ?? "").length;

      const inputClassName = useMemo(
        () =>
          cn(
            "input flex h-10",
            error
              ? "border-red-600 focus:border-red-600 focus:ring-red-200"
              : null,
            active && "border-purple-500 ring-2 ring-ring",
            className,
          ),
        [error, active, className],
      );

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;
        if (onValueChange) {
          onValueChange(newValue);
        }
        if (onChange) {
          onChange(e);
        }
      };

      return (
        <div className="w-full">
          <input
            type={type}
            className={inputClassName}
            ref={ref}
            value={value}
            onChange={handleChange}
            maxLength={maxLength}
            {...props}
          />
          {maxLength && maxLength > 1 && (
            <div className="mt-1 text-right text-sm text-gray-500 tabular-nums">
              {currentLength}/{maxLength}
            </div>
          )}
        </div>
      );
    },
  ),
);

Input.displayName = "Input";
